import React, { useEffect } from 'react';
import { format } from 'date-fns';
import { useSystem } from '../../contexts/SystemContext';
import { useAuth } from '../../hooks/useAuth';

export function QueueTab() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const {
    queueStats,
    loading,
    error,
    fetchSystemData,
  } = useSystem();

  // Top-level AdminDashboard manages polling. Only do initial load.
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchSystemData();
    }
  }, [isAuthenticated, authLoading, fetchSystemData]);

  if (error) {
    return (
      <div className="tc-card p-6">
        <div className="text-center text-red-600">
          <p>Error: {error}</p>
          <button onClick={fetchSystemData} className="tc-btn tc-btn-primary mt-4">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Queue Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="tc-card tc-card-hover p-6">
          <h3 className="text-xl font-semibold mb-4 tc-text-primary">Queue Statistics</h3>
          {loading && !queueStats ? (
            <div className="text-center py-4">
              <div className="tc-spinner mb-2"></div>
              <p className="tc-text-secondary text-sm">Loading queue stats...</p>
            </div>
          ) : queueStats ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="tc-text-secondary">Pending</span>
                <span className="font-medium px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                  {queueStats.pending}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="tc-text-secondary">Processing</span>
                <span className="font-medium px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                  {queueStats.processing}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="tc-text-secondary">Completed</span>
                <span className="font-medium px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                  {queueStats.completed}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="tc-text-secondary">Failed</span>
                <span className="font-medium px-2 py-1 bg-red-100 text-red-800 rounded-full text-sm">
                  {queueStats.failed}
                </span>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="tc-text-secondary">No queue data available</p>
            </div>
          )}
        </div>

        <div className="tc-card tc-card-hover p-6">
          <h3 className="text-xl font-semibold mb-4 tc-text-primary">Queue Health</h3>
          <div className="space-y-4">
            <div className="tc-bg-secondary rounded-lg p-4">
              <p className="text-sm tc-text-secondary">Worker Status</p>
              <div className="flex items-center mt-1">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                <p className="text-lg font-medium tc-text-primary">Active</p>
              </div>
            </div>
            <div className="tc-bg-secondary rounded-lg p-4">
              <p className="text-sm tc-text-secondary">Last Health Check</p>
              <p className="text-lg font-medium tc-text-primary mt-1">
                {format(new Date(), 'HH:mm:ss')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Queue Status Banner */}
      {queueStats && (queueStats.processing > 0 || queueStats.pending > 0) && (
        <div className="tc-card p-4 border-l-4 border-blue-400 bg-blue-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="tc-spinner w-5 h-5 mr-3"></div>
              <div>
                <p className="text-sm font-medium text-blue-800">
                  Queue Processing Active
                </p>
                <p className="text-xs text-blue-600">
                  {queueStats.processing} job(s) running, {queueStats.pending} job(s) waiting
                </p>
              </div>
            </div>
            <button
              onClick={fetchSystemData}
              className="tc-btn tc-btn-secondary text-sm"
            >
              🔄 Refresh
            </button>
          </div>
        </div>
      )}

      {/* Queue Performance Metrics */}
      <div className="tc-card p-6">
        <h3 className="text-lg font-medium tc-text-primary mb-4">Performance Metrics</h3>
        {queueStats ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-2xl font-bold tc-text-primary">
                {queueStats.pending + queueStats.processing}
              </div>
              <div className="text-sm tc-text-secondary">Active Jobs</div>
            </div>
            
            <div>
              <div className="text-2xl font-bold text-green-600">
                {queueStats.completed}
              </div>
              <div className="text-sm tc-text-secondary">Completed Today</div>
            </div>
            
            <div>
              <div className="text-2xl font-bold text-red-600">
                {queueStats.failed}
              </div>
              <div className="text-sm tc-text-secondary">Failed Jobs</div>
            </div>
            
            <div>
              <div className="text-2xl font-bold tc-text-primary">
                {queueStats.completed + queueStats.failed > 0 
                  ? ((queueStats.completed / (queueStats.completed + queueStats.failed)) * 100).toFixed(1)
                  : '100'
                }%
              </div>
              <div className="text-sm tc-text-secondary">Success Rate</div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="tc-text-secondary">No performance data available</p>
          </div>
        )}
      </div>

      {/* Queue Information */}
      <div className="tc-card p-6">
        <h3 className="text-lg font-medium tc-text-primary mb-4">Queue Information</h3>
        <div className="prose prose-sm tc-text-secondary">
          <ul className="space-y-2">
            <li><strong>News Scraping Jobs:</strong> Fetch articles from RSS feeds and APIs</li>
            <li><strong>Article Analysis Jobs:</strong> Process articles through AI for risk assessment</li>
            <li><strong>Risk Calculation Jobs:</strong> Calculate daily risk scores and trends</li>
            <li><strong>Queue Processing:</strong> Jobs are processed automatically every 5 seconds</li>
            <li><strong>Health Monitoring:</strong> Failed jobs are retried with exponential backoff</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
